import React, { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavigateOptions, useLocation, useNavigate } from 'react-router-dom';
import { bindToggle } from '@arema/components/Util';
import { Icon, IconName, Menu, Input, Modal } from 'react-frontier2';
import { CDN_URL } from './AdminConfig';
import { TitleProvider } from '@arema/components/Hooks'
import { PrinterProvider, useUser } from './AdminHooks';
import API from './API';

import './style/header.scss';
import UserAccess from './UserAccess';
import LoginResponse from './screens/LoginResponse';

interface MenuItem{
	text: string,
	access?: number[],
	href?: string,
	items?: MenuItem[],
	special?: boolean,
};

const DEFAULT_MENU : MenuItem[] = [
	{ 
		text: 'Eventos', items: [
			{ text: 'Buscador', 		href: '/events',			access: [UserAccess.EVENTS.VIEW_EVENTS], },
			{ text: 'Padres', 		href: '/parent',			access: [UserAccess.EVENTS.VIEW_EVENT_GROUP], },
			{ text: 'Promotores',	href: '/promoters',		access: [UserAccess.CATALOGS.PROMOTER_VIEW], },
			{ text: 'Recintos', 		href: '/venues',			access: [UserAccess.VENUES.VIEW_VENUES], },
		]
	},
	{
		text: 'Ordenes', items: [
			{ text: 'Buscar', 		href: '/orders',			access: [UserAccess.ORDERS.VIEW_ORDERS], },
			{ text: 'Reembolsos', 	href: '/refunds',			access: [UserAccess.ORDERS.VIEW_REFUND], },
		]
	},
	{ text: 'Stats',	 		 		href: '/stats',			access: [UserAccess.ADMIN.STATISTICS], },
	{ text: 'Catálogos', 			special: true },
	{
		text: 'Contabilidad', items: [
			{ text: 'Conciliar', 			href: '/conciliacion',						access: [UserAccess.CONTABILIDAD.ADD_DEPOSIT], },
			{ text: 'Taquillas', 			href: '/taquillas',							access: [UserAccess.CONTABILIDAD.VIEW_PDV_DEPOSIT], },
			{ text: 'Liquidaciones', 		href: '/payouts',								access: [UserAccess.CONTABILIDAD.VIEW_PAYOUTS], },
			{ text: 'Tabla del Día', 		href: '/conciliacion/tabla',				access: [UserAccess.CONTABILIDAD.VIEW_DAY_TABLE], },
			{ text: 'Papeleta', 				href: '/conciliacion/papeleta',			access: [UserAccess.CONTABILIDAD.VIEW_PAPELETA], },
			{ text: 'Facturas', 				href: '/reportes/facturacion',			access: [UserAccess.REPORTS_ADMIN.INVOICE], },
		]
	},
	{
		text: 'Herramientas', items: [
			// { text: 'Callcenter', 			href: '/tools/callcenter',				access: [UserAccess.SELLERS.ACCESS_CALLCENTER], },
			{ text: 'Filas', 					href: '/queues',							access: [], },
			{ text: 'Config', 				href: '/config',					access: [UserAccess.ADMIN.DEVELOPER], },
			// { text: 'Reenviador', 			href: '/tools/resender',				access: [UserAccess.ORDERS.MASSIVE_RESEND_EMAILS], },
		]
	},
	{ text: 'Reportes',				href: '/reportes',		access: [UserAccess.REPORTS_ADMIN.ACCESS] },
]

interface CatalogItem{
	name: string,
	href: string,
	icon: IconName,
	access: number,
}

var CATALOGS : CatalogItem[] = [
	{ name: 'Usuarios', 					href: '/users', 				icon: 'user-cog',				access: UserAccess.USERS.VIEW_USERS },
	{ name: 'Puntos de Venta', 		href: '/pdv', 					icon: 'cash-register',		access: UserAccess.PDV.VIEW_PDV},
	{ name: 'Checadores', 				href: '/scanners', 			icon: 'qrcode',				access: UserAccess.CATALOGS.SCANNER_VIEW },
	{ name: 'Formas Envio', 			href: '/c/delivery', 		icon: 'box',					access: UserAccess.CATALOGS.DELIVERY_METHODS_VIEW },
	// { name: 'Cupones', 					href: '/c/coupons', 			icon: 'tag',					access: UserAccess.CATALOGS.COUPONS_VIEW },
	{ name: 'Recintos', 					href: '/venues', 				icon: 'home',					access: UserAccess.VENUES.VIEW_VENUES },
	{ name: 'Eventos', 					href: '/events', 				icon: 'theater-masks',		access: UserAccess.EVENTS.VIEW_EVENTS },
	{ name: 'Eventos Padre', 			href: '/parent',				icon: 'folder-open',			access: UserAccess.EVENTS.VIEW_EVENT_GROUP },
	{ name: 'Liquidaciones', 			href: '/payouts',				icon: 'money-check-alt',	access: UserAccess.CONTABILIDAD.VIEW_PAYOUTS },
	// { name: 'Templates', 				href: '/templates',			icon: 'ticket alt',			access: UserAccess.TOOLS.TEMPLATES_VIEW },
	{ name: 'Zonas',						href: '/zones',				icon: 'object-group',		access: UserAccess.CATALOGS.ZONES_VIEW },
	{ name: 'Bancos',						href: '/banks',				icon: 'building-columns',	access: UserAccess.CATALOGS.ZONES_VIEW },
	{ name: 'Terminales Bancarias',	href: '/terminals',			icon: 'credit-card',			access: UserAccess.CATALOGS.TERMINALS_VIEW },
	// { name: 'Vendedores', 				href: '/c/sellers', 			icon: 'user tag' },
	// { name: 'Carrusel', 					href: '/c/carousel', 		icon: 'image' },
];

interface ComponentProps extends PropsWithChildren{
	outlet: JSX.Element
}

interface SidebarMenuProps{
	text: string,
	items: MenuItem['items']
}

var SidebarMenu = (props: SidebarMenuProps)=>{
	var [shown, setShown] = useState<boolean>(false)

	return <div className="item dropdown">
		<div className="content" onClick={bindToggle(shown, setShown)}>
			<div className="text">{props.text}</div>
			<Icon name='caret-down' />
		</div>
		{shown ? (
			<div className="menu">
				{props.items.map(a=>(
					<Link className={`item`} to={a.href} key={`hdrmenu-item-${a.href}`} replace>{a.text}</Link>
				))}
			</div>
		) : null}
	</div>
}

var Navigation = (props: ComponentProps)=>{
	var { user, hasAccess, access, refresh } = useUser();

	var [searchModal, setSearchModal] = useState<boolean>(false);
	var [searchText, setSearchText] = useState<string>('');
	var [sidebar, setSidebar] = useState<boolean>(false);

	var searchInput = useRef<HTMLInputElement>();
	var location = useLocation();

	useEffect(()=>{
		setSearchModal(false);
		setSidebar(false);
	}, [location.pathname]);

	var user_catalogs = useMemo(()=>{
		var catalogs : CatalogItem[] = CATALOGS;
		if(searchText){
			catalogs = CATALOGS.filter(function(a){
				return a.name.toLowerCase().includes(searchText.toLowerCase());
			});
		}
		return catalogs.filter(a=>!a.access || hasAccess(a.access));
	}, [CATALOGS, user, access, searchText]);
	
	var logout = ()=>{
		API.logout();
	}

	var is_auth = !user && window.location.pathname==='/auth';
	if(is_auth){
		return <LoginResponse />
	}

	if(!user){
		return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: '#f0f0f0' }}>
			<img src={`${CDN_URL}/logo/LogoG_Small.png`} alt="AREMA" style={{ borderRadius: 4 }} />
		</div>
	}
	
	var menu = [];
	for(var i of DEFAULT_MENU){
		if(i.items){
			var items = [];
			for(var m of i.items){
				if(hasAccess(m.access)) items.push(m);
			}
			if(items.length>0){
				if(items.length==1) menu.push(items[0])
				else menu.push({ text: i.text, items })
			}
		}else if(hasAccess(i.access)){
			menu.push(i);
		}
	}

	var showDebug = ()=>{
		console.log("SHOW DEBUG!");
	}

	var showSearch = ()=>{
		setSearchModal(true);
		setSearchText('');
		setSidebar(false);
	}
	
	return <TitleProvider suffix='AREMA Admin'>
		<PrinterProvider>
			<div style={{ backgroundColor: '#f7f7f7', minHeight: '100vh', position: 'relative' }}>
				<div className="ar menu">
					<div className="left">
						<div className="hamburger" onClick={bindToggle(sidebar, setSidebar)}>
							<Icon name='bars' />
						</div>
						<Link className="icon" to={'/'} replace>
							<img src={`${CDN_URL}/logo/LogoW_Small.png`} alt="Logo" />
						</Link>
						{menu.map(a=>{
							if(a.items && a.items.length!=1){
								if(a.items.length==0) return null;
								return <Menu
									key={`hdrmenu-drop-${a.text}`}
									trigger={(
										<div className='item'>
											{a.text}
											<Icon name='caret-down' style={{ marginLeft: 5, marginRight: 0 }} />
										</div>
									)}
									items={a.items.map(a=>({
										text: a.text,
										as: Link,
										to: a.href,
										key: `hdrmenu-item-${a.href}`
									}))}
								/>
							}else{
								var item = a.items && a.items.length==1 ? a.items[0] : a;
								return item.special ? (
									user_catalogs.length>0 ? (
										<div className={`item`} onClick={showSearch} key={`hdrmenu-item-${item.href}`}>{item.text}</div>
									) : null
								) : (
									<Link className={`item`} to={item.href} key={`hdrmenu-item-${item.href}`} replace>{item.text}</Link>
								)
							}
						})}
						{hasAccess(UserAccess.ADMIN.DEVELOPER) && (
							<div className="icon item" onClick={showDebug}>
								<Icon name="bug" style={{ color: 'blue', fontSize: 20 }} />
							</div>
						)}
					</div>
					<div className="right">
						{user_catalogs.length>0 ? (
							<div className="icon item" onClick={showSearch}>
								<Icon name='search' size={20} />
							</div>
						) : null}
						<Menu
							trigger={(
								<div className='item'>
									{(user.first_name || user.email)}
									<Icon name='caret-down' style={{ marginRight: 0, marginLeft: 5, lineHeight: '12px' }} />
								</div>
							)}
							items={[
								// { text: 'Contraseña', as: Link, to: '' }
								{ text: 'Logout', onClick: logout }
							]}
						/>
					</div>
				</div>
				{sidebar ? (
					<div className="ar menu-sidebar">
						{menu.map((a, i)=>{
							if(a.items && a.items.length!=1){
								if(a.items.length==0) return null;
								return <SidebarMenu text={a.text} items={a.items} key={`shdrmenu-drop-item-${i}`} />
							}else{
								var item = a.items && a.items.length==1 ? a.items[0] : a;
								return item.special ? (
									<div className='item' onClick={showSearch} key={`shdrmenu-item-${item.href}`}>{item.text}</div>
								) : (
									<Link className='item' to={item.href} key={`shdrmenu-item-${item.href}`} replace={true}>{item.text}</Link>
								)
							}
						})}
					</div>
				) : null}
				<div style={{ paddingTop: 50, paddingBottom: 50, minHeight: '100vh' }}>
					{props.outlet}
					{/* <RouterProvider router={RouterNav} /> */}
				</div>
				<Modal open={searchModal} onClose={()=>setSearchModal(false)} size="tiny" className='search catalog'>
					<Modal.Input
						autoFocus 
						// ref={searchInput}
						value={searchText} 
						inputType='search' 
						onChange={setSearchText} 
						iconName="search" 
						placeholder='Buscar en AREMA...' 
						style={{ marginBottom: 0 }}
					/>
					<Modal.Content style={{ padding: 0, height: 400, overflow: 'scroll' }}>
						<div className="fr selectable striped divided items" style={{ marginTop: 0 }}>
							{user_catalogs.map((a, i)=>(
								<Link to={a.href} className="item" key={`clg-${a.href}-${i}`} replace={true}>
									<Icon name={a.icon} style={{ lineHeight: '14px', marginRight: 10 }} />
									{a.name}
								</Link>
							))}
						</div>
					</Modal.Content>
				</Modal>
			</div>
		</PrinterProvider>
	</TitleProvider>
}

export default Navigation;